import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

interface AdminGuardProps {
  children: ReactNode;
}

/**
 * Deja pasar solo a los administradores.
 *
 * Antes, `/admin` estaba protegido únicamente por `AuthGuard`, que solo
 * comprueba "¿hay alguien con sesión?". Cualquier persona que se
 * registrara y escribiera /admin en la barra de direcciones entraba al
 * panel.
 *
 * Esta guarda es la mitad visible del arreglo, y por sí sola no
 * alcanzaría: cualquier comprobación hecha en el navegador se puede
 * saltar. La que de verdad protege es la de la base de datos, que no
 * devuelve las estadísticas a quien no tiene el rol. Esto solo evita
 * mostrarle una pantalla rota a quien no debería estar acá.
 */
export default function AdminGuard({ children }: AdminGuardProps) {
  const { user, loading } = useAuth();
  const [esAdmin, setEsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) {
      setEsAdmin(null);
      return;
    }
    let vigente = true;
    supabase.rpc('es_admin').then(({ data, error }) => {
      if (vigente) setEsAdmin(!error && data === true);
    });
    return () => { vigente = false; };
  }, [user]);

  if (loading || (user && esAdmin === null)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4 text-center">Comprobando permisos…</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  // A quien no es administrador se le manda a su panel, sin decirle que
  // esta dirección existe.
  if (!esAdmin) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
}
